import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signInAnonymously,
  User,
} from "firebase/auth";
import { auth } from "@/Utils/firebase";
import { logError, logInfo } from "@/Utils/logger";
import { tagHotjarIdentity } from "@/Utils/hotjar";

// Magic-link route owns its own sign-in (signInWithCustomToken with the
// redeemed owner token). The anon endpoint flow AND the anon safety net must
// not run there: a rescue-anon uid ≠ scan owner ⇒ empty scan list / blank
// preview that misdiagnoses as an outage. See HERE_BE_DRAGONS §magic-link.
const isMagicLinkRoute = () => window.location.pathname.startsWith("/redeem");

// Define the shape of the AuthContext
interface AuthContextType {
  user: User | null;
  fetchIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  load: boolean;
}

// Create the context with an initial value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [load, setLoading] = useState(true);
  const [endpointCalled, setEndpointCalled] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  function getFirebaseAnonymousUID() {
    return new Promise((resolve) => {
      try {
        // Open the Firebase IndexedDB database
        const dbRequest = indexedDB.open("firebaseLocalStorageDb");

        dbRequest.onerror = () => {
          console.error("Failed to open Firebase IndexedDB");
          resolve(null);
        };

        dbRequest.onsuccess = (event: any) => {
          const db = event.target.result;

          // Check if the firebaseLocalStorage object store exists
          if (!db.objectStoreNames.contains("firebaseLocalStorage")) {
            console.error("firebaseLocalStorage object store not found");
            db.close();
            resolve(null);
            return;
          }

          const transaction = db.transaction(
            ["firebaseLocalStorage"],
            "readonly"
          );
          const store = transaction.objectStore("firebaseLocalStorage");

          // Get all records from the store
          const getAllRequest = store.getAll();

          getAllRequest.onsuccess = () => {
            const records = getAllRequest.result;

            // Look for Firebase auth user records
            for (const record of records) {
              // Check if this is a Firebase auth user key
              if (
                record.fbase_key &&
                record.fbase_key.startsWith("firebase:authUser:")
              ) {
                const userData = record.value;

                // Check if user is anonymous and has a UID
                if (userData && userData.isAnonymous && userData.uid) {
                  console.log("Found anonymous Firebase UID:", userData.uid);
                  db.close();
                  resolve(userData.uid);
                  return;
                }
              }
            }

            console.log("No anonymous Firebase user found in IndexedDB");
            db.close();
            resolve(null);
          };

          getAllRequest.onerror = () => {
            console.error("Failed to read from firebaseLocalStorage");
            db.close();
            resolve(null);
          };
        };
      } catch (error) {
        console.error("Error accessing Firebase IndexedDB:", error);
        resolve(null);
      }
    });
  }

  // Alternative synchronous version using localStorage fallback
  function getFirebaseAnonymousUIDSync() {
    try {
      const uid = localStorage.getItem("anonymous-user-id");
      return uid;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return null;
    }
  }

  // Combined function for maximum compatibility
  const getAnonymousUID = useCallback(async () => {
    // Try synchronous localStorage first
    const syncUID = getFirebaseAnonymousUIDSync();
    if (syncUID) {
      return syncUID;
    }

    // Fallback to IndexedDB
    const asyncUID = await getFirebaseAnonymousUID();
    return asyncUID;
  }, []);

  const callAuthEndPoint = useCallback(
    async (signal?: AbortSignal) => {
      const uid = await getAnonymousUID();
      const authEndpoint = process.env.REACT_APP_AUTH_ENDPOINT;
      if (authEndpoint) {
        const uidHeader = uid ? String(uid) : "";
        const response = await fetch(authEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-anonymous-user-id": uidHeader,
          },
          signal, // Add abort signal to fetch request
        });
        const data = await response.json();
        if (data?.userId) {
          localStorage.setItem("anonymous-user-id", data.userId);
        }
        return data;
      }
      return null;
    },
    [getAnonymousUID]
  );

  useEffect(() => {
    let abortController: AbortController | null = null;

    // Listen to authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        logInfo("[auth] onAuthStateChanged: user signed in", {
          uidPrefix: currentUser.uid?.slice(0, 8) ?? null,
          isAnonymous: currentUser.isAnonymous,
          providerCount: currentUser.providerData?.length ?? 0,
        });
        setUser(currentUser);
        // Tag the active Hotjar recording with the anon uid (and the widget
        // sessionId if it's already present). Idempotent; if the sessionId
        // arrives later it re-fires via the ttf:sharing-updated event. See
        // src/Utils/hotjar.ts.
        tagHotjarIdentity();
        setLoading(false);
        setEndpointCalled(true); // Prevent endpoint call since user is already signed in
        setAuthInitialized(true);

        // Abort any ongoing endpoint request
        if (abortController) {
          abortController.abort();
        }
      } else {
        logInfo("[auth] onAuthStateChanged: no user, will attempt endpoint authentication");
        setUser(null);
        setAuthInitialized(true);
      }
    });

    // Post-condition safety net. Called unconditionally at the end of every
    // auth-flow path. If we reach this with auth.currentUser still null,
    // run signInAnonymously as a last resort. This is the single guarantee
    // that "after the auth flow runs, the session has a Firebase user."
    //
    // Why this is necessary (May-2026 Darren incident, prod GKE LB logs):
    //   POST /anon-user returned 200 + a valid 1065-byte customToken body,
    //   but the iOS 18.7 / Safari 26.4 client still ended up with
    //   auth.currentUser === null when the scan upload ran 2-5 min later.
    //   signInWithCustomToken resolved without throwing (otherwise the
    //   original catch-block anon fallback would have rescued), but the
    //   Firebase auth state did not stick — most likely an iOS Safari
    //   IndexedDB / persistence quirk. With no post-condition check the
    //   PWA proceeded to a fully unauthenticated scan, the rotation
    //   completed, and the upload died with "User is not authenticated!"
    //   wiping all 22 captured frames. The post-condition closes that
    //   class of failure regardless of the underlying cause.
    const ensureSignedInOrNet = async (signal: AbortSignal) => {
      if (signal.aborted) return;
      // Never run the anon safety net on /redeem: that route signs the user in
      // as the scan OWNER; a rescue-anon uid here would mask the real session.
      if (isMagicLinkRoute()) return;
      if (auth.currentUser) return;
      logInfo("[auth] Safety-net: currentUser still null after auth flow, attempting signInAnonymously");
      try {
        await signInAnonymously(auth);
        logInfo("[auth] Safety-net: signInAnonymously succeeded");
      } catch (e) {
        logError("[auth] Safety-net: signInAnonymously failed", {
          name: (e as Error)?.name,
          message: (e as Error)?.message,
        });
        setLoading(false);
      }
    };

    const getAndLoadUser = async (signal: AbortSignal) => {
      if (isMagicLinkRoute()) {
        logInfo(
          "[magic-link] auth-context: skipping anon endpoint flow on /redeem"
        );
        setEndpointCalled(true);
        setLoading(false);
        return;
      }
      try {
        // Double-check if user is already signed in or endpoint already called
        if (auth.currentUser || endpointCalled) {
          return;
        }

        if (signal.aborted) return;

        logInfo("[auth] Calling auth endpoint");
        const data = await callAuthEndPoint(signal);

        if (signal.aborted) return;

        logInfo("[auth] Auth endpoint resolved", {
          hasCustomToken: !!data?.customToken,
          hasUserId: !!data?.userId,
          isNewUser: !!data?.isNewUser,
        });

        if (data?.customToken) {
          try {
            await signInWithCustomToken(auth, data.customToken);
            // Explicit cast: the early-return narrowing of auth.currentUser
            // to `null` does NOT widen back across the await, even via a
            // typed local. Use `as User | null` to break the narrowing.
            const cu = auth.currentUser as User | null;
            logInfo("[auth] signInWithCustomToken resolved", {
              currentUserAfter: cu ? "set" : "null",
              uidPrefix: cu ? cu.uid.slice(0, 8) : null,
            });
          } catch (signInErr) {
            logError("[auth] signInWithCustomToken threw", {
              name: (signInErr as Error)?.name,
              message: (signInErr as Error)?.message,
            });
            // Fall through to the post-condition net below.
          }
        }
        setEndpointCalled(true);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        logError("[auth] Auth endpoint threw", {
          name: (error as Error)?.name,
          message: (error as Error)?.message,
        });
        setEndpointCalled(true);
        // Fall through to the post-condition net below.
      }

      // Single safety net for every exit path (no-customToken, customToken
      // throw, endpoint throw, OR customToken resolved but currentUser still
      // null). Replaces the previous catch-only signInAnonymously fallback.
      await ensureSignedInOrNet(signal);
    };

    // Only attempt endpoint authentication after auth state is initialized
    // and no user is signed in
    if (authInitialized && !auth.currentUser && !endpointCalled) {
      abortController = new AbortController();
      getAndLoadUser(abortController.signal);
    }

    return () => {
      unsubscribe();
      if (abortController) {
        abortController.abort();
      }
    };
  }, [auth, endpointCalled, authInitialized, callAuthEndPoint]);

  useEffect(() => {
    let mounted = true;

    // If already authenticated or endpoint was called, don't schedule.
    if (auth.currentUser || endpointCalled) return;
    if (authInitialized) {
      // The above effect will take care of user auth.
      return;
    }

    let controller = new AbortController();

    const timeoutId = window.setTimeout(async () => {
      if (isMagicLinkRoute()) {
        logInfo(
          "[magic-link] auth-context: skipping anon endpoint flow on /redeem"
        );
        setEndpointCalled(true);
        setLoading(false);
        return;
      }
      // Re-check at fire time
      if (auth.currentUser || endpointCalled) return;

      logInfo("[auth] 3s timeout fired — auth never initialized, attempting endpoint call");
      try {
        const data = await callAuthEndPoint(controller?.signal);
        if (!mounted) return;

        logInfo("[auth] Timeout endpoint resolved", {
          hasCustomToken: !!data?.customToken,
          hasUserId: !!data?.userId,
        });
        setEndpointCalled(true);

        if (data?.customToken && !auth.currentUser) {
          try {
            await signInWithCustomToken(auth, data.customToken);
            // See note in primary effect: type assertion to break the
            // post-await narrowing of auth.currentUser.
            const cu = auth.currentUser as User | null;
            logInfo("[auth] Timeout signInWithCustomToken resolved", {
              currentUserAfter: cu ? "set" : "null",
              uidPrefix: cu ? cu.uid.slice(0, 8) : null,
            });
          } catch (signInErr) {
            logError("[auth] Timeout signInWithCustomToken threw", {
              name: (signInErr as Error)?.name,
              message: (signInErr as Error)?.message,
            });
            // Fall through to safety net below.
          }
        }
      } catch (err) {
        logError("[auth] Timeout auth endpoint threw", {
          name: (err as Error)?.name,
          message: (err as Error)?.message,
        });
        setEndpointCalled(true);
        // Fall through to safety net below.
      }

      // Same post-condition net as in the primary effect: if we reach here
      // and currentUser is still null, sign in anonymously.
      if (!mounted) return;
      if (controller.signal.aborted) return;
      if (auth.currentUser) return;
      logInfo("[auth] Timeout safety-net: currentUser still null, attempting signInAnonymously");
      try {
        await signInAnonymously(auth);
        logInfo("[auth] Timeout safety-net: signInAnonymously succeeded");
      } catch (e) {
        logError("[auth] Timeout safety-net: signInAnonymously failed", {
          name: (e as Error)?.name,
          message: (e as Error)?.message,
        });
        setLoading(false);
      }
    }, 3000);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointCalled, authInitialized]);

  // Function to fetch the latest ID token
  const fetchIdToken = async (forceRefresh = false): Promise<string | null> => {
    if (!auth.currentUser) {
      console.error("No user is signed in.");
      return null;
    }
    try {
      const token = await auth.currentUser.getIdToken(forceRefresh);
      return token;
    } catch (error) {
      console.error("Error fetching ID token:", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, fetchIdToken, load }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
